import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { getRouteCnName } from '@/utils/route-metadata';
import { getCurrentScrollPercentage } from '@/utils/scroll';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Sparkly Eyeglasses专题页神策埋点Hook
 * 对应Vue版本中sparkly-eyeglasses/index.vue的神策埋点功能
 */
export function useSparklyEyeglassesTracking() {
  const { trackEvent } = useReporting();
  const pathname = usePathname();
  const { getFromRouterInfo } = useRouteInfo();
  // 页面浏览状态跟踪
  const themePageViewSended = useRef(false);
  const enterTimer = useRef(Date.now());
  // 记录最后的滚动状态，避免页面跳转时滚动位置被重置
  const lastScrollState = useRef({ viewAdvance: 0 });

  /**
   * 神策埋点：专题页页面浏览
   * 对应Vue版本中的SDPageView函数
   * @TrackType sensorsdata
   * @EventType ThemePage_View
   * @EventDesc 专题页页面浏览
   * @EventParams {
   *   page_name: 页面名称,
   *   refer_page_name: 前向页面名称,
   *   refer_url_path: 前向页面路径,
   * }
   * @Tapd 1010887
   */
  const trackThemePageView = useCallback(() => {
    try {
      if (themePageViewSended.current) return;
      themePageViewSended.current = true;

      // 重置开始时间
      enterTimer.current = Date.now();
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      trackEvent(
        {
          eventName: 'ThemePage_View',
          properties: {
            page_name: getRouteCnName(pathname),
            refer_page_name,
            refer_url_path,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('ThemePage_View tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * 神策埋点：专题页页面离开
   * 对应Vue版本中的SDPageLeave函数
   * @TrackType sensorsdata
   * @EventType ThemePage_Leave
   * @EventDesc 专题页页面离开
   * @EventParams {
   *   page_name: 页面名称,
   *   view_advance: 浏览进度,
   *   view_duration: 浏览时长,
   * }
   */
  const trackThemePageLeave = useCallback(() => {
    try {
      if (!themePageViewSended.current) return;
      themePageViewSended.current = false;

      // 计算浏览时长（秒）
      const view_duration = Math.ceil((Date.now() - enterTimer.current) / 1000);

      const { viewAdvance } = lastScrollState.current;

      trackEvent(
        {
          eventName: 'ThemePage_Leave',
          properties: {
            page_name: getRouteCnName(pathname),
            view_advance: viewAdvance,
            view_duration,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('ThemePage_Leave tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * 神策埋点：专题页商品坑位曝光
   * 对应Vue版本中的ThemePageExp函数
   * @TrackType sensorsdata
   * @EventType ThemePageProductPits_Exp
   * @EventDesc 专题页商品坑位曝光
   */
  const trackThemePageProductExp = useCallback(
    (params: { item: any; index: number; module_name: string; image_type: string }) => {
      try {
        trackEvent(
          {
            eventName: 'ThemePageProductPits_Exp',
            properties: {
              page_name: getRouteCnName(pathname),
              module_name: params.module_name,
              pits_index: params.index,
              image_type: params.image_type,
              shop_sku: params.item.shop_sku,
              business_sku: params.item.business_sku,
              product_sku: params.item.sku,
              product_id: `${params.item.id}`,
              product_name: params.item.short_name,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('ThemePageProductPits_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：专题页商品坑位点击
   * 对应Vue版本中的ThemePageClick函数
   * @TrackType sensorsdata
   * @EventType ThemePageProductPits_Click
   * @EventDesc 专题页商品坑位点击
   */
  const trackThemePageProductClick = useCallback(
    (params: { item: any; index: number; module_name: string; image_type: string }) => {
      try {
        trackEvent(
          {
            eventName: 'ThemePageProductPits_Click',
            properties: {
              page_name: getRouteCnName(pathname),
              module_name: params.module_name,
              pits_index: params.index,
              image_type: params.image_type,
              shop_sku: params.item.shop_sku,
              business_sku: params.item.business_sku,
              product_sku: params.item.sku,
              product_id: `${params.item.id}`,
              product_name: params.item.short_name,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('ThemePageProductPits_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：专题页广告坑位曝光
   * 对应Vue版本中的SDThemePagePageADPits_Exp函数
   * @TrackType sensorsdata
   * @EventType ThemePagePageADPits_Exp
   * @EventDesc 专题页广告坑位曝光
   */
  const trackThemePageADExp = useCallback(
    (params: { item: any; index: number }) => {
      try {
        trackEvent(
          {
            eventName: 'ThemePagePageADPits_Exp',
            properties: {
              page_name: getRouteCnName(pathname),
              module_name: '新品系列',
              mkt_pits_index: params.index,
              mkt_pits_jump_type: `${params.item.jump_type}`,
              mkt_pits_jump_content: `${params.item.jump_value}`,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('ThemePagePageADPits_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：专题页广告坑位点击
   * 对应Vue版本中的SDThemePageADPits_Click函数
   * @TrackType sensorsdata
   * @EventType ThemePageADPits_Click
   * @EventDesc 专题页广告坑位点击
   */
  const trackThemePageADClick = useCallback(
    (params: { item: any; index: number }) => {
      try {
        trackEvent(
          {
            eventName: 'ThemePageADPits_Click',
            properties: {
              page_name: getRouteCnName(pathname),
              module_name: '新品系列',
              mkt_pits_index: params.index,
              mkt_pits_jump_type: `${params.item.jump_type}`,
              mkt_pits_jump_content: `${params.item.jump_value}`,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('ThemePageADPits_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 重置专题页浏览状态
   * 用于页面重新激活时重置状态
   */
  const resetThemePageViewState = useCallback(() => {
    themePageViewSended.current = false;
    enterTimer.current = Date.now();
  }, []);

  // 定期更新滚动状态，避免页面跳转时丢失滚动信息
  useEffect(() => {
    const updateScrollState = () => {
      try {
        const viewAdvance = getCurrentScrollPercentage();
        lastScrollState.current = { viewAdvance };
      } catch (error) {
        // 忽略错误，使用默认值
        logger.error('Error updating scroll state:', error);
      }
    };

    // 立即更新一次
    updateScrollState();

    // 监听滚动事件，实时更新滚动状态
    const handleScroll = () => {
      updateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return {
    // 页面级埋点
    trackThemePageView,
    trackThemePageLeave,
    // 商品级埋点
    trackThemePageProductExp,
    trackThemePageProductClick,
    // 广告级埋点
    trackThemePageADExp,
    trackThemePageADClick,
    // 辅助函数
    resetThemePageViewState,
  };
}
